import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import { yupTrimToNull } from "src/utils/stringUtils";
import SimpleDropdown from "src/apps/commons/dropdowns/components/SimpleDropdown";
import { IRemyndCategoryTreeLv3 } from "../../remyndCategory/types";
import { IRemyndCategoryPartnerMapSaveReq } from "../types";
import { EditModalState } from "./ListContainers";

type FormData = {
  partnerLv1Name: string;
  partnerLv2Name: string;
  partnerLv3Name: string;
  lv3Id: RemyndCategoryLv3Id;
};

const schema = yup.object().shape({
  lv3Id: yup
    .string()
    .transform(yupTrimToNull)
    .required("กรุณาเลือก หมวดสินค้า Level 3")
});

interface Props {
  editState: EditModalState;
  handleCloseModal: () => void;
  dropdowns: IDropdown<IRemyndCategoryTreeLv3>[];
  callSubmit: (
    id: RemyndCategoryPartnerMapId | undefined,
    req: IRemyndCategoryPartnerMapSaveReq
  ) => Promise<boolean>;
  callDelete: (req: ICommonIdsReq) => Promise<boolean>;
}

const MappingModal: React.FC<Props> = props => {
  const { editState, dropdowns, handleCloseModal, callSubmit, callDelete } =
    props;

  const [dropdown, setDropdown] =
    useState<IDropdown<IRemyndCategoryTreeLv3> | null>(null);

  const defaultFormData = useMemo<Partial<FormData>>(() => {
    return {
      partnerLv1Name: `${editState.lv1?.label}`,
      partnerLv2Name: `${editState.lv2?.label}`,
      partnerLv3Name: `${editState.lv3?.label}`,
      lv3Id: editState.mapping?.remyndLv3Id ?? ""
    };
  }, [editState]);

  const [submitting, setSubmitting] = useState(false);
  const {
    formState: { errors },
    handleSubmit,
    control,
    reset
  } = useForm<Partial<FormData>>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    reset(defaultFormData);
    if (editState.mapping) {
      setDropdown(
        dropdowns.find(d => d.value === editState.mapping?.remyndLv3Id) ?? null
      );
    } else {
      setDropdown(null);
    }
  }, [reset, editState, dropdowns, defaultFormData]);

  const [closing, setClosing] = useState(false);
  const handleRequestClose = () => setClosing(true);
  const handleCompleteClose = () => {
    handleCloseModal();
    setClosing(false);
    setDropdown(null);
  };

  const handleTransformAndSubmit = async (form: FormData) => {
    setSubmitting(true);
    const partnerLv3Id = editState.lv3?.id;
    const success = partnerLv3Id
      ? await callSubmit(editState.mapping?.id, {
          remyndLv3Id: form.lv3Id,
          partnerLv3Id: partnerLv3Id,
          snapPartnerNameTh: editState.lv3?.nameTh,
          snapPartnerNameEn: editState.lv3?.nameEn,
          snapRemyndNameTh: dropdown?.meta?.nameTh,
          snapRemyndNameEn: dropdown?.meta?.nameEn
        })
      : false;
    if (success) {
      handleRequestClose();
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const mappingId = editState.mapping?.id;
    if (mappingId) {
      const success = await callDelete({ ids: [mappingId] });
      if (success) {
        handleRequestClose();
      }
    }
    setSubmitting(false);
  };

  const isOpen = editState.open;
  const isAdd = !editState.mapping;
  return (
    <Dialog
      open={closing ? false : isOpen}
      onClose={handleRequestClose}
      onExited={handleCompleteClose}
      maxWidth="md"
      fullWidth
      disableBackdropClick
      disableEscapeKeyDown
      aria-labelledby="form-dialog-title"
    >
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <DialogTitle id="form-dialog-title" disableTypography>
          <Typography variant="h3">
            {`${isAdd ? "เพิ่ม" : "แก้ไข"} Mapping หมวดสินค้า`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="partnerLv1Name"
                control={control}
                defaultValue={defaultFormData.partnerLv1Name}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled
                    {...field}
                    label="ชื่อหมวดหมู่ Partner Level 1"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="partnerLv2Name"
                control={control}
                defaultValue={defaultFormData.partnerLv2Name}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled
                    {...field}
                    label="ชื่อหมวดหมู่ Partner Level 2"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="partnerLv3Name"
                control={control}
                defaultValue={defaultFormData.partnerLv3Name}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    disabled
                    {...field}
                    label="ชื่อหมวดหมู่ Partner Level 3"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="lv3Id"
                control={control}
                defaultValue={defaultFormData.lv3Id}
                render={({ field: { onChange } }) => (
                  <SimpleDropdown
                    disabled={submitting}
                    label="หมวดสินค้า Remynd Level 3 *"
                    options={dropdowns}
                    onChange={dropdown => {
                      onChange(dropdown?.value);
                      setDropdown(dropdown);
                    }}
                    value={dropdown}
                    error={Boolean(errors.lv3Id)}
                    helperText={errors.lv3Id?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleRequestClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          {editState?.mapping && (
            <Button
              variant="contained"
              onClick={handleDelete}
              disabled={submitting}
            >
              Delete Mapping
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={submitting}
            type="submit"
            startIcon={
              submitting ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MappingModal;
